import React, { useCallback } from 'react'
import { Grid, TextareaAutosize } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import PropTypes from 'prop-types'
import PageHeading from '../../common/PageHeading'
import Divider from '../../../../../components/atoms/Divider'
import SydLabel, { hookFormErrorAdapter } from '../../../../../components/commonDesign/SydLabel'
import FlexRow from '../../../../../components/molecules/FlexRow'
import SydButton from '../../../../../components/commonDesign/Button'
import SydInput from '../../../../../components/commonDesign/SydInput'
import FlexColumn from '../../../../../components/molecules/FlexColumn'
import OpalHeading from '../../../../../components/opal/OpalHeading'
import CheckboxList from '../../common/form/CheckboxList'
import { useModifyAiScheduledDigest } from '../../../../../api/denaliAssistant'

const useSubmitter = (form, scheduleId, onComplete) => {
  const { handleSubmit, clearErrors } = form
  const { mutateAsync: modifyScheduledDigest, isLoading } = useModifyAiScheduledDigest()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      scheduleId,
      name: formData.name || null,
      prompt: formData.prompt || null,
      schedules: formData.schedules || []
    }

    try {
      form?.clearErrors?.()
      const result = await modifyScheduledDigest(command)
      onComplete(result)
    } catch (err) {
      form.setError('root', {
        type: 'custom',
        message: err.message || 'Something unexpected happened while creating the scheduled digest.'
      })
    }
  }, [form, modifyScheduledDigest, onComplete, scheduleId])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }
    clearErrors()
    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, clearErrors, onSubmit])

  return {
    submitter,
    processing: isLoading
  }
}

function EditDigestForm ({ scheduledPrompt, availableSchedules, onComplete, onCancel }) {
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: scheduledPrompt.name,
      prompt: scheduledPrompt.prompt,
      schedules: availableSchedules.map(x => x.value).filter(x => scheduledPrompt.configuration.schedules.includes(x))
    }
  })
  const { submitter, processing } = useSubmitter(form, scheduledPrompt.scheduleId, onComplete)

  return (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <PageHeading title='Edit Digest' />
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='name'
            control={form.control}
            rules={{
              maxLength: { value: 64, message: 'Maximum 64 characters' }
            }}
            render={({ field }) => (
              <SydLabel label='Name' description='Leave blank for automatically chosen name'>
                <SydInput
                  name='name'
                  disabled={processing}
                  {...(field)}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <Controller
            name='prompt'
            control={form.control}
            rules={{
              required: { value: true, message: 'Please enter a prompt.' }
            }}
            render={({ field, fieldState }) => (
              <SydLabel label='Your prompt' required error={hookFormErrorAdapter(form, fieldState)}>
                <TextareaAutosize
                  disabled={processing}
                  name='intent'
                  style={{ width: '100%' }}
                  minRows={10}
                  {...(field)}
                />
              </SydLabel>
            )}
          />
        </Grid>
        <Grid item xs={12}>
          <FlexColumn className='gap-sm'>
            <Controller
              name='schedules'
              control={form.control}
              rules={{
                minLength: { value: 1, message: 'You must select at least 1 schedule.' }
              }}
              render={({ field, fieldState }) => (
                <SydLabel label='Included Scheduled Prompts' required error={hookFormErrorAdapter(form, fieldState)}>
                  <CheckboxList options={availableSchedules} {...field} />
                </SydLabel>
              )}
            />
            {availableSchedules.map(x => (
              <div key={x.scheduleId}>
                <OpalHeading className='font-serif font-size-md'>{x.name}</OpalHeading>
              </div>
            ))}
          </FlexColumn>
        </Grid>
        <Grid item xs={12}>
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <FlexRow className='gap-md justify-end'>
            <SydButton size='lg' variant='ghost' onClick={onCancel} disabled={processing}>Cancel</SydButton>
            <SydButton size='lg' variant='primary' onClick={submitter} processing={processing}>Edit Digest</SydButton>
          </FlexRow>
        </Grid>
      </Grid>
    </>
  )
}

EditDigestForm.propTypes = {
  scheduledPrompt: PropTypes.object,
  availableSchedules: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string,
    data: PropTypes.any
  })),
  onComplete: PropTypes.func,
  onCancel: PropTypes.func
}

export default EditDigestForm
